import {
  type AddOnKey,
  type IAddOnConfig,
  type IAddOnPriceConfig,
} from '@principle-theorem/principle-core/interfaces';
import type Stripe from 'stripe';

/** A resolved catalog hit: which add-on a Stripe price belongs to. */
export interface IAddOnPriceCatalogEntry {
  addOnKey: AddOnKey;
  priceConfig: IAddOnPriceConfig;
}

/**
 * Stripe product catalog helpers
 */
export class AddOnConfig {
  /**
   * Reverse-lookup from a Stripe `price_*` id to the add-on that sells it.
   * Returns `undefined` when the price is absent from the catalog, which
   * callers treat as an unrecognised price rather than a failure.
   */
  static findPriceEntry(
    priceId: string,
    addOnConfigs: Record<AddOnKey, IAddOnConfig>
  ): IAddOnPriceCatalogEntry | undefined {
    for (const addOnKey of Object.keys(addOnConfigs) as AddOnKey[]) {
      const priceConfig = addOnConfigs[addOnKey].prices.find(
        (price) => price.priceId === priceId
      );

      if (priceConfig) {
        return { addOnKey, priceConfig };
      }
    }
  }

  /**
   * Whether a price bills one of the usage meters configured for this
   * environment.
   *
   * A subscription's items are a mix of add-on prices and usage prices, since
   * SMS usage is metered on the same subscription that carries the add-ons. A
   * usage price sells no add-on, so it has no catalog entry and
   * {@link findPriceEntry} cannot classify it.
   *
   * Prices are matched on the meter they bill rather than on `usage_type`,
   * because add-ons are sold on metered prices too.
   */
  static isUsageMeterPrice(
    price: Stripe.Price,
    usageMeterIds: readonly string[]
  ): boolean {
    const meterId = price.recurring?.meter;

    return meterId ? usageMeterIds.includes(meterId) : false;
  }
}
